import { useEffect, useMemo, useRef } from "react";
import { useForm, useFormState } from "react-hook-form";

import { SelectOptionTypes } from "../../../../components/Input";
import {
  FormPanel,
  discardCopyOf,
  formPanelStatusOf,
} from "../../../../components/FormPanel";
import {
  buildDraftDetails,
  ConfirmDetail,
  joinDetailParts,
} from "../../../../components/PopUp/confirmShared";
import { useGetAccountsQuery } from "../../../../services";
import { FUNDING_ACCOUNT_TYPES } from "../../../Accounts/types";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import {
  calculatePrincipal,
  DateFormat,
  formatCurrency,
  formatDate,
} from "../../../../utils";
import { PaymentFormFields } from "./PaymentFormFields";
import {
  ACTION_LABEL,
  DEBOUNCE_DELAY_MS,
  FORM_ID,
  PANEL_EYEBROW,
} from "./constants";
import {
  PaymentObject,
  PaymentValidationRules,
  UpdatePaymentProps,
} from "./types";
import { currentRateOf } from "../../helpers";

// A payment already made from a savings or investment account keeps that source as an option.
const useAccountsData = (currentSource: SelectOptionTypes | null) => {
  const { data, isLoading, isError, refetch } = useGetAccountsQuery();

  const fundingOptions: SelectOptionTypes[] = (data ?? [])
    .filter((account) => FUNDING_ACCOUNT_TYPES.includes(account.accountType))
    .map((account) => ({
      value: String(account.id),
      label: account.name,
    }));
  const accountOptions =
    currentSource === null ||
    fundingOptions.some((option) => option.value === currentSource.value)
      ? fundingOptions
      : [...fundingOptions, currentSource];

  return { accountOptions, isLoading, isError, refetch };
};

export const PaymentPanel = ({
  title,
  payment,
  liability,
  isOpen,
  onClose,
  handleOnSubmit,
}: UpdatePaymentProps) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const { accountOptions, isLoading, isError, refetch } = useAccountsData(
    payment?.id ? payment.fromAccount : null,
  );

  // Pre-populate from the liability's next payment when creating; existing
  // payments (edit / duplicate) keep their own values.
  const createDefaults = useMemo<PaymentObject>(() => {
    const principal = liability?.nextPaymentPrincipal;
    const interest = liability?.nextPaymentInterest;
    const hasSplit =
      principal !== null &&
      principal !== undefined &&
      interest !== null &&
      interest !== undefined;
    const total =
      liability?.paymentAmount ?? (hasSplit ? principal + interest : null);

    return {
      paymentDate: liability?.nextPaymentDate
        ? new Date(liability.nextPaymentDate)
        : new Date(),
      amount: total !== null && total !== undefined ? String(total) : "",
      principalAmount:
        principal !== null && principal !== undefined ? String(principal) : "",
      fromAccount: null,
      note: "",
      setPrincipalManually: false,
    };
  }, [liability]);

  const initialValues = payment ?? createDefaults;

  const form = useForm<PaymentObject>({
    defaultValues: initialValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues, setValue, watch } = form;
  const { isSubmitting } = useFormState({ control });

  const isEdit = Boolean(payment?.id);
  const amountValue = watch("amount");
  const setManually = watch("setPrincipalManually");

  // Skip the very first auto-calc so the pre-populated principal from the next
  // payment is preserved; recalculate on later amount edits (auto mode only).
  const skipInitialCalc = useRef(true);

  useEffect(() => {
    if (isEdit || !liability || setManually) {
      return;
    }
    if (skipInitialCalc.current) {
      skipInitialCalc.current = false;
      return;
    }

    const amount = Number.parseFloat(String(amountValue ?? ""));
    if (!Number.isFinite(amount) || amount <= 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const interestRate =
        currentRateOf(liability.interestRateHistory)?.interestRate ?? 0;
      const currentBalance = liability.currentAmount ?? 0;

      if (interestRate === 0) {
        setValue("principalAmount", String(amount), {
          shouldValidate: false,
          shouldDirty: true,
        });
        return;
      }

      const calculatedPrincipal = calculatePrincipal(
        currentBalance,
        interestRate,
        amount,
      );
      setValue("principalAmount", calculatedPrincipal.toFixed(2), {
        shouldValidate: false,
        shouldDirty: true,
      });
    }, DEBOUNCE_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [amountValue, setManually, liability, isEdit, setValue]);

  const validationRules = useMemo<PaymentValidationRules>(
    () => ({
      amount: {
        required: "Amount is required",
        min: { value: 0.01, message: "Amount must be greater than 0" },
      },
      fromAccount: {
        required: "From account is required",
      },
      principalAmount: {
        required: "Principal Amount Value is required",
        validate: {
          isBelowOrEqual: (value) => {
            const amount = Number.parseFloat(getValues("amount"));
            const principalAmount = Number.parseFloat(value);
            if (principalAmount > amount) {
              return "Cannot exceed the Amount!";
            }
            return true;
          },
          notExceedBalance: (value) => {
            if (!liability?.currentAmount) {
              return true;
            }
            const principalAmount = Number.parseFloat(value);
            if (principalAmount > liability.currentAmount) {
              return "Cannot exceed remaining balance!";
            }
            return true;
          },
        },
      },
      paymentDate: {
        required: "Payment date is required",
      },
      note: {
        minLength: {
          value: 3,
          message: "Description must be at least 3 characters",
        },
        maxLength: {
          value: 255,
          message: "Description cannot exceed 255 characters",
        },
      },
    }),
    [getValues, liability],
  );

  const eyebrow = isEdit ? title : PANEL_EYEBROW;
  const headingTitle = liability?.name ?? title;

  const buildDiscardDetails = (): ConfirmDetail => {
    const formValues = getValues();
    const amountNumber = Number.parseFloat(formValues.amount);
    const hasAmount = !Number.isNaN(amountNumber) && amountNumber > 0;
    const formattedAmount = hasAmount
      ? formatCurrency(-amountNumber)
      : undefined;

    if (isEdit) {
      return {
        pill: { label: "PAYMENT", variant: "EXPENSE" },
        title: "Loan payment",
        subtitle: joinDetailParts(
          formValues.note,
          formatDate(formValues.paymentDate, DateFormat.DMY_SPACE),
        ),
        amount: formattedAmount,
        amountVariant: "expense",
      };
    }

    return buildDraftDetails({
      noun: "payment",
      fieldValues: [
        formValues.amount,
        formValues.principalAmount,
        formValues.paymentDate,
        formValues.fromAccount,
        formValues.note,
      ],
      amount: formattedAmount,
    });
  };

  const discard = {
    ...discardCopyOf(isEdit, "payment"),
    details: buildDiscardDetails,
  };

  const load = {
    status: formPanelStatusOf({ isLoading, isError }),
    onRetry: refetch,
    loadingLabel: "Loading accounts…",
    errorMessage: "Failed to load accounts. Please try again.",
  };

  const fields = (
    <PaymentFormFields
      control={control}
      isSubmitting={isSubmitting}
      isCompact={isCompact}
      accountOptions={accountOptions}
      currentAmount={liability?.currentAmount}
      validationRules={validationRules}
    />
  );

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Loan payment form"
      eyebrow={eyebrow}
      title={headingTitle}
      form={form}
      resetValues={initialValues}
      formId={FORM_ID}
      onSubmit={handleOnSubmit}
      submitLabel={ACTION_LABEL}
      discard={discard}
      mobile={{ title: headingTitle, submitLabel: ACTION_LABEL, fields }}
      load={load}
    >
      {fields}
    </FormPanel>
  );
};
